import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DuprEditor } from "@/components/DuprEditor";
import { ArrowLeft, ArrowRight, ShieldCheck, User as UserIcon, Home, Phone, Calendar, Zap, MapPin, Check } from "lucide-react";
import { toast } from "sonner";
import MediaConsentEditor from "@/components/MediaConsentEditor";
import ProfileImageUpload from "@/components/ProfileImageUpload";
import { MyCreditsPanel } from "@/pages/MyCredits";

const SEX_OPTIONS = [
  { id: "male", label: "Male" },
  { id: "female", label: "Female" },
  { id: "prefer_not_to_say", label: "Prefer not to say" },
];

function isUnder18(iso) {
  if (!iso) return false;
  const dob = new Date(iso);
  if (isNaN(dob.getTime())) return false;
  const t = new Date();
  let age = t.getFullYear() - dob.getFullYear();
  const m = t.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && t.getDate() < dob.getDate())) age--;
  return age < 18;
}

export default function PersonalDetails() {
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") === "credits" ? "credits" : "details";
  const returnTo = params.get("return") || "/dashboard";
  const isOnboarding = window.location.pathname.startsWith("/onboarding");

  const [form, setForm] = useState({
    first_name: "", surname: "", full_name: "",
    dob: "", sex: "", phone: "",
    address_line1: "", address_line2: "", city: "", postcode: "", country: "United Kingdom",
    guardian_name: "", guardian_phone: "", guardian_email: "",
    gdpr_consent: false,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/me/personal-details").then(({ data }) => {
      const pd = data.personal_details || {};
      // Backfill first_name / surname from existing full_name for legacy accounts.
      let first = pd.first_name || "";
      let surname = pd.surname || "";
      if (!first && !surname && (pd.full_name || user?.name)) {
        const parts = (pd.full_name || user?.name || "").trim().split(/\s+/);
        if (parts.length >= 2) {
          first = parts.slice(0, -1).join(" ");
          surname = parts[parts.length - 1];
        } else {
          first = parts[0] || "";
        }
      }
      setForm((f) => ({ ...f, ...pd, first_name: first, surname }));
    }).catch(() => {});
     
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const minor = isUnder18(form.dob);

  // Iter93 — nearby regions auto-suggestion. When the postcode is at least
  // partly filled (≥ 3 chars, letters/digits only), debounce a hit against
  // `/regions/nearby?postcode=…&radius_miles=50` so we can prompt the player
  // with "Regions near you — set your home region in one click".
  const [nearby, setNearby] = useState({ loading: false, results: [], error: "" });
  const [homeRegionId, setHomeRegionId] = useState(user?.home_region_id || "");
  useEffect(() => setHomeRegionId(user?.home_region_id || ""), [user?.home_region_id]);

  useEffect(() => {
    const pc = (form.postcode || "").replace(/\s+/g, "").toUpperCase();
    if (pc.length < 3) { setNearby({ loading: false, results: [], error: "" }); return; }
    let cancelled = false;
    setNearby((s) => ({ ...s, loading: true, error: "" }));
    const handle = setTimeout(async () => {
      try {
        const { data } = await api.get(`/regions/nearby`, { params: { postcode: pc, radius_miles: 50 } });
        if (cancelled) return;
        setNearby({ loading: false, results: data?.results || [], error: "" });
      } catch (e) {
        if (cancelled) return;
        setNearby({ loading: false, results: [], error: formatApiError(e.response?.data?.detail) || "" });
      }
    }, 600);
    return () => { cancelled = true; clearTimeout(handle); };
  }, [form.postcode]);

  const setHomeRegion = async (region) => {
    try {
      await api.put("/me/home-region", { region_id: region.id });
      setHomeRegionId(region.id);
      // Refresh the AuthContext user so downstream pages (Offerings filter)
      // pick up the new default without a hard reload.
      try {
        const me = await api.get("/auth/me");
        setUser(me.data);
      } catch (_e) { /* ignore */ }
      toast.success(`Home region set — ${region.label}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't set home region");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name?.trim() || !form.surname?.trim()) {
      toast.error("Enter your first name and surname");
      return;
    }
    if (!form.gdpr_consent) {
      toast.error("Please tick the GDPR consent to save");
      return;
    }
    if (minor && (!form.guardian_name || !form.guardian_phone)) {
      toast.error("Players under 18 must provide guardian contact details");
      return;
    }
    setSaving(true);
    try {
      // Send both split fields + combined full_name so downstream still works.
      const payload = {
        ...form,
        first_name: form.first_name.trim(),
        surname: form.surname.trim(),
        full_name: `${form.first_name.trim()} ${form.surname.trim()}`.trim(),
      };
      await api.put("/me/personal-details", payload);
      // Refresh user — profile_completed flag flips + name may have updated
      try {
        const me = await api.get("/auth/me");
        setUser(me.data);
      } catch (_e) { /* ignore */ }
      toast.success("Profile details saved");
      nav(returnTo, { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not save");
    } finally { setSaving(false); }
  };

  const isCoachOrAdmin = user?.role === "coach" || user?.role === "admin";

  // IMPORTANT: keep the body markup outside of any inner-defined component.
  // Previously `Page` was defined inside the parent, which made React re-mount the
  // entire form on every state change → inputs lost focus after each keystroke,
  // making the address feel like it only accepts one character at a time.
  const body = (
    <>
      {isOnboarding && <div className="label-mono mb-2">STEP 2 OF 2 · PERSONAL DETAILS</div>}
      <div className="label-mono mb-2" style={{ color: "#CCFF00" }}>
        <UserIcon className="w-3.5 h-3.5 inline mr-1" /> {isOnboarding ? "ALMOST THERE" : "YOUR INFO"}
      </div>
      <h1 className="h-display text-4xl sm:text-5xl tracking-tighter mb-2">
        {isOnboarding ? "Tell us about you." : "Personal details."}
      </h1>
      <p className="text-sm text-[#A3A3A3] mb-8 max-w-xl">
        {isCoachOrAdmin
          ? "We need the same info from coaches as players (address, phone, DOB) for insurance, safeguarding records and payouts. Only Rally Lab admins (and you) ever see this."
          : "Required before your first booking. Only you, your coaches and Rally Lab admins see this."}
      </p>

      <form onSubmit={submit} className="space-y-6">
        {/* Iter79: Profile photo upload — appears at the top of the personal
            details form so it's captured for both students and coaches
            during onboarding. Skippable; saved independently via its own
            endpoint (no submit dependency). */}
        <div className="card-surface p-4">
          <div className="label-mono text-[10px] mb-3 text-[#CCFF00]">PROFILE PHOTO</div>
          <ProfileImageUpload />
          <p className="text-[10px] text-[#A3A3A3] mt-3 leading-relaxed">
            Optional — but it helps your coach recognise you on the court. JPG / PNG up to 8MB.
          </p>
        </div>
        {/* Identity */}
        <section className="card-surface p-6 space-y-4">
          <div className="label-mono">IDENTITY</div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="First name" required testid="pd-firstname"
              value={form.first_name} onChange={(v) => update("first_name", v)} placeholder="e.g. Rob" />
            <Field label="Surname" required testid="pd-surname"
              value={form.surname} onChange={(v) => update("surname", v)} placeholder="e.g. Smith" />
            <Field label="Date of birth" required type="date" testid="pd-dob"
              value={form.dob} onChange={(v) => update("dob", v)} icon={Calendar} />
          </div>
          <div>
            <div className="label-mono mb-2">Sex <span className="text-[#FF4500]">*</span></div>
            <div className="flex gap-2 flex-wrap">
              {SEX_OPTIONS.map((o) => {
                const on = form.sex === o.id;
                return (
                  <button key={o.id} type="button" onClick={() => update("sex", o.id)}
                    data-testid={`pd-sex-${o.id}`}
                    className="px-4 py-2.5 text-xs uppercase tracking-wider border transition-colors"
                    style={on ? { background: "#CCFF00", color: "#0A0A0A", borderColor: "#CCFF00", fontWeight: 700 } : { borderColor: "#262626", color: "#A3A3A3" }}>
                    {o.label}
                  </button>
                );
              })}
            </div>
          </div>
          <Field label="Phone number" required testid="pd-phone" type="tel"
            value={form.phone} onChange={(v) => update("phone", v)} placeholder="+44 7…" icon={Phone} />
        </section>

        {/* Address */}
        <section className="card-surface p-6 space-y-4">
          <div className="label-mono flex items-center gap-2"><Home className="w-3.5 h-3.5 text-[#CCFF00]" /> ADDRESS</div>
          <Field label="Address line 1" required testid="pd-addr1"
            value={form.address_line1} onChange={(v) => update("address_line1", v)} placeholder="House number / street" />
          <Field label="Address line 2 (optional)" testid="pd-addr2"
            value={form.address_line2} onChange={(v) => update("address_line2", v)} />
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="City / town" required testid="pd-city"
              value={form.city} onChange={(v) => update("city", v)} />
            <Field label="Postcode" required testid="pd-postcode"
              value={form.postcode} onChange={(v) => update("postcode", v.toUpperCase())} placeholder="M1 1AA" />
          </div>
          <Field label="Country" required testid="pd-country"
            value={form.country} onChange={(v) => update("country", v)} />
        </section>

        {/* Iter93 — Nearby regions suggestion. Rendered once the postcode has
            ≥3 chars so the loading/empty/error branches are all reachable. One
            click sets home region so the Offerings filter defaults to it. */}
        {((form.postcode || "").replace(/\s+/g, "").length >= 3) && (
          <section className="card-surface p-6 space-y-3" data-testid="nearby-regions-section">
            <div className="label-mono flex items-center gap-2">
              <MapPin className="w-3.5 h-3.5 text-[#CCFF00]" /> REGIONS NEAR YOU
            </div>
            {nearby.loading && (
              <div className="text-xs text-[#A3A3A3]" data-testid="nearby-regions-loading">Looking up regions within 50 miles of {form.postcode?.toUpperCase()}…</div>
            )}
            {!nearby.loading && nearby.error && (
              <div className="text-xs text-[#FF4500]" data-testid="nearby-regions-error">{nearby.error}</div>
            )}
            {!nearby.loading && !nearby.error && nearby.results.length === 0 && form.postcode && (
              <div className="text-xs text-[#A3A3A3]" data-testid="nearby-regions-empty">
                No Rally Lab regions within 50 miles of that postcode yet. We&apos;ll pop one open once demand builds.
              </div>
            )}
            {!nearby.loading && nearby.results.length > 0 && (
              <>
                <div className="text-xs text-[#A3A3A3]">
                  Tap the closest one to set it as your home region — the Offerings tab will default to sessions there.
                </div>
                <div className="grid sm:grid-cols-2 gap-2" data-testid="nearby-regions-list">
                  {nearby.results.slice(0, 6).map((r) => {
                    const active = homeRegionId === r.id;
                    return (
                      <button
                        key={r.id}
                        type="button"
                        onClick={() => setHomeRegion(r)}
                        data-testid={`nearby-region-${r.slug}`}
                        className="text-left border p-3 transition-colors flex items-start justify-between gap-3"
                        style={active
                          ? { borderColor: "#CCFF00", background: "rgba(204,255,0,0.08)" }
                          : { borderColor: "#262626" }}
                      >
                        <div className="min-w-0">
                          <div className="text-sm text-white truncate flex items-center gap-2">
                            {r.label}
                            {active && <Check className="w-3.5 h-3.5 text-[#CCFF00]" />}
                          </div>
                          <div className="text-[10px] uppercase tracking-wider text-[#A3A3A3] mt-0.5">
                            {r.distance_miles} mi away{r.parent_region ? ` · ${r.parent_region}` : ""}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </section>
        )}

        {/* Guardian for minors */}
        {minor && (
          <section className="card-surface p-6 space-y-4 border-l-4 border-[#FF4500]" data-testid="pd-guardian-section">
            <div className="label-mono flex items-center gap-2" style={{ color: "#FF4500" }}>
              ⚠ GUARDIAN DETAILS (REQUIRED FOR UNDER-18s)
            </div>
            <p className="text-xs text-[#A3A3A3]">
              We need a parent or legal guardian we can reach for any session-related communication.
            </p>
            <Field label="Guardian full name" required testid="pd-guardian-name"
              value={form.guardian_name} onChange={(v) => update("guardian_name", v)} />
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Guardian phone" required testid="pd-guardian-phone" type="tel"
                value={form.guardian_phone} onChange={(v) => update("guardian_phone", v)} />
              <Field label="Guardian email (optional)" testid="pd-guardian-email" type="email"
                value={form.guardian_email} onChange={(v) => update("guardian_email", v)} />
            </div>
          </section>
        )}

        {user?.role === "student" && <DuprEditor />}

        {/* GDPR */}
        <section className="card-surface p-6">
          <label className="flex items-start gap-3 cursor-pointer" data-testid="pd-gdpr-label">
            <input type="checkbox" checked={!!form.gdpr_consent}
              onChange={(e) => update("gdpr_consent", e.target.checked)}
              data-testid="pd-gdpr"
              className="mt-1 w-5 h-5 accent-[#CCFF00] shrink-0" />
            <span className="text-sm text-[#E5E5E5] leading-relaxed">
              <span className="font-bold text-white">I consent to Rally Lab storing my personal data</span> for the purpose of session bookings, communications, and (where applicable) safeguarding. Data is retained per the <Link to="/privacy" className="text-[#CCFF00] hover:underline">Privacy Policy</Link>. I can request deletion at any time by emailing the platform owner.
            </span>
          </label>
          {form.gdpr_consent_at && (
            <div className="mt-3 text-[10px] text-[#525252] uppercase tracking-wider">
              First consented: {new Date(form.gdpr_consent_at).toLocaleString()}
            </div>
          )}
        </section>

        <div className="border border-[#262626] p-3 flex items-start gap-2 text-xs text-[#A3A3A3]">
          <ShieldCheck className="w-4 h-4 text-[#CCFF00] mt-0.5 shrink-0" />
          Only you and Rally Lab admins can see these details. Your coach sees your name and email — nothing else.
        </div>

        {/* Media consent — editable anytime. Withdrawing consent flags the profile
            for coaches (server-side pinned coach-note); re-granting removes the flag. */}
        <MediaConsentEditor />

        <button type="submit" disabled={saving} data-testid="pd-submit"
          className="btn-volt w-full py-4 text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50">
          {saving ? "Saving…" : "Save & continue"} <ArrowRight className="w-4 h-4" />
        </button>
      </form>
    </>
  );

  if (isOnboarding) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white noise-bg" data-testid="personal-details-page">
        <div className="max-w-3xl mx-auto px-6 py-12">
          <Link to="/" data-testid="pd-back-home" className="flex items-center gap-2 mb-10 hover:opacity-80 transition-opacity w-fit">
            <div className="w-8 h-8 bg-[#CCFF00] flex items-center justify-center"><Zap strokeWidth={2.5} className="w-5 h-5 text-black" /></div>
            <span className="h-display text-2xl tracking-tighter">RALLY LAB · PERSONAL DETAILS</span>
          </Link>
          {body}
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-6" data-testid="personal-details-page">
      <Link to="/dashboard" data-testid="pd-back-home" className="inline-flex items-center gap-2 text-xs text-[#A3A3A3] hover:text-white uppercase tracking-wider">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to dashboard
      </Link>
      {user?.role === "student" && (
        <div className="flex gap-1 border-b border-[#262626]" role="tablist" data-testid="profile-tabs">
          {[["details", "Personal details"], ["credits", "My Credits"]].map(([k, label]) => (
            <button key={k} type="button" role="tab" aria-selected={tab === k} data-testid={`profile-tab-${k}`}
              onClick={() => setParams(k === "credits" ? { tab: "credits" } : {}, { replace: true })}
              className={`px-4 py-2.5 text-xs uppercase tracking-wider border-b-2 -mb-px transition-colors ${tab === k ? "border-[#CCFF00] text-white" : "border-transparent text-[#A3A3A3] hover:text-white"}`}>
              {label}
            </button>
          ))}
        </div>
      )}
      {user?.role === "student" && tab === "credits" ? <MyCreditsPanel /> : body}
    </div>
  );
}

function Field({ label, value, onChange, type = "text", placeholder, required, testid, icon: Icon, disabled = false, hint = "" }) {
  return (
    <div>
      <div className="label-mono mb-2">{label}{required && <span className="text-[#FF4500]"> *</span>}</div>
      <div className="relative">
        {Icon && <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#525252]" />}
        <input type={type} value={value || ""} required={required}
          onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
          disabled={disabled}
          readOnly={disabled}
          data-testid={testid}
          className={`w-full bg-[#141414] border border-[#262626] py-2.5 text-sm focus:outline-none focus:border-[#CCFF00] ${Icon ? "pl-9 pr-3" : "px-3"} ${disabled ? "opacity-60 cursor-not-allowed" : ""}`} />
      </div>
      {hint && <div className="text-[10px] text-[#A3A3A3] mt-1" data-testid={`${testid}-hint`}>{hint}</div>}
    </div>
  );
}
